import { RenderedHtml } from 'modules/editor';
import React from 'react';
import { Check, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { PassageReview } from './PassageReview';

/** Returns null for types it doesn't handle — the caller falls back to its own option/match rendering. */

interface Props {
  type: string;
  answerRaw: unknown;
  correctAnswerRaw: unknown;
  questionData: Record<string, any> | null | undefined;
  /** PASSAGE only: extra content under one sub-question, by its id. */
  renderSubQuestionFooter?: (subQuestionId: string) => React.ReactNode;
}

const REVIEWED_TYPES = new Set([
  'FILL_IN_BLANKS',
  'OPEN_ENDED',
  'REORDER',
  'PASSAGE',
  'SHORT_ANSWER',
  'MATCHING',
  'MULTIPLE_CHOICE',
  'MULTIPLE_RESPONSE',
  'TRUE_FALSE',
]);

export const canReview = (type: string): boolean => REVIEWED_TYPES.has(type);

const asRecord = (v: unknown): Record<string, string> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, string>) : {};
const asArray = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);
const norm = (s: unknown): string =>
  String(s ?? '')
    .normalize('NFC')
    .replace(/[‘’ʻʼ`]/g, "'")
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

interface RowProps {
  label?: React.ReactNode;
  given?: React.ReactNode;
  correct?: React.ReactNode;
  ok?: boolean;
}

const Row: React.FC<RowProps> = ({ label, given, correct, ok }) => {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border p-3 text-sm transition-colors',
        ok === true && 'border-green-200 bg-green-50/40 dark:border-green-900/50 dark:bg-green-950/20',
        ok === false && 'border-red-200 bg-red-50/40 dark:border-red-900/50 dark:bg-red-950/20',
        ok === undefined && 'border-border bg-card',
      )}
    >
      <div className="mt-0.5 shrink-0">
        {ok === undefined ? null : ok ? (
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
        ) : (
          <X className="h-4 w-4 text-red-500 dark:text-red-400" />
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-2.5">
        {label != null && <div className="font-semibold text-foreground">{label}</div>}

        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground">
            {t('your_answer')}
          </div>
          <div className={cn('break-words', ok === false ? 'text-red-600 dark:text-red-400' : 'text-foreground')}>
            {given || <span className="text-muted-foreground italic">— {t('not_answered')} —</span>}
          </div>
        </div>

        {ok === false && correct != null && (
          <div className="space-y-1 pt-1.5 border-t border-dashed border-border/70">
            <div className="text-xs font-medium text-success-700 dark:text-success-400">
              {t('correct_answer')}
            </div>
            <div className="break-words text-green-700 dark:text-green-400 font-medium">
              {correct}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const QuestionAnswerReview: React.FC<Props> = ({
  type,
  answerRaw,
  correctAnswerRaw,
  questionData,
  renderSubQuestionFooter,
}) => {
  const { t } = useTranslation();
  const qd = questionData || {};

  switch (type) {
    case 'FILL_IN_BLANKS': {
      const blanks: Array<{ id: string; acceptedAnswers?: string[] }> = qd.blanks || [];
      const given = asRecord(answerRaw);
      return (
        <div className="space-y-2">
          {blanks.map((b, i) => {
            const ans = given[b.id] ?? '';
            const accepted = b.acceptedAnswers || [];
            const ok =
              Boolean(ans.trim()) &&
              (!qd.maxWords || ans.trim().split(/\s+/).length <= qd.maxWords) &&
              accepted.some((acc) => norm(acc) === norm(ans));
            return (
              <Row
                key={b.id}
                label={`${t('reading_blank')} ${i + 1}`}
                given={ans.trim() ? <RenderedHtml html={ans} inline /> : null}
                correct={
                  accepted.length > 0 ? (
                    <span className="space-x-1">
                      {accepted.map((acc, idx) => (
                        <React.Fragment key={idx}>
                          {idx > 0 && <span className="text-muted-foreground font-normal"> / </span>}
                          <RenderedHtml html={acc} inline />
                        </React.Fragment>
                      ))}
                    </span>
                  ) : null
                }
                ok={ok}
              />
            );
          })}
        </div>
      );
    }

    case 'REORDER': {
      const items: Array<{ id: string; content: string }> = qd.reorderItems || [];
      const correctOrder: string[] = qd.correctOrder || [];
      const givenOrder = asArray(answerRaw);
      const content = (id: string) => items.find((it) => it.id === id)?.content ?? id;
      const isAnswered = givenOrder.length > 0;
      const ok = isAnswered && JSON.stringify(givenOrder) === JSON.stringify(correctOrder);

      const renderList = (order: string[], isCorrectList: boolean) => (
        <ol className="space-y-1.5 list-none m-0 p-0">
          {order.map((id, idx) => (
            <li
              key={id || idx}
              className={cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm border',
                isCorrectList
                  ? 'border-green-200 bg-green-50/70 text-green-900 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-200'
                  : 'border-border/60 bg-muted/40 text-foreground',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  isCorrectList
                    ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-100'
                    : 'bg-muted-foreground/20 text-muted-foreground',
                )}
              >
                {idx + 1}
              </span>
              <span className="min-w-0 flex-1 break-words">
                <RenderedHtml html={content(id)} inline />
              </span>
            </li>
          ))}
        </ol>
      );

      return (
        <div className="space-y-2">
          <Row
            given={isAnswered ? renderList(givenOrder, ok) : null}
            correct={correctOrder.length > 0 ? renderList(correctOrder, true) : null}
            ok={isAnswered ? ok : false}
          />
        </div>
      );
    }

    case 'OPEN_ENDED': {
      const ans = typeof answerRaw === 'string' ? answerRaw : asArray(answerRaw).join(', ');
      return (
        <div className="space-y-2">
          <div className="rounded-lg border p-3 text-sm bg-card">
            <div className="mb-1 text-xs font-medium text-muted-foreground">
              {t('your_answer')}
            </div>
            {ans.trim() ? (
              <RenderedHtml html={ans} className="text-foreground" />
            ) : (
              <span className="italic text-muted-foreground">— {t('not_answered')} —</span>
            )}
          </div>
          {qd.rubric && (
            <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground bg-muted/20">
              <span className="font-semibold text-foreground">{t('rubric_criteria')}: </span>
              <RenderedHtml html={qd.rubric} inline />
            </div>
          )}
        </div>
      );
    }

    case 'SHORT_ANSWER': {
      const given = typeof answerRaw === 'string' ? answerRaw : '';
      const accepted =
        qd.acceptedAnswers ?? (typeof correctAnswerRaw === 'string' ? [correctAnswerRaw] : []);
      const ok =
        qd.answerMode !== 'exact'
          ? undefined
          : Boolean(given.trim()) &&
            (!qd.maxWords || given.trim().split(/\s+/).length <= qd.maxWords) &&
            accepted.some((answer: string) => norm(answer) === norm(given));
      return (
        <Row
          given={given.trim() ? <RenderedHtml html={given} inline /> : null}
          correct={
            accepted.length > 0 ? (
              <span className="space-x-1">
                {accepted.map((answer: string, idx: number) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-muted-foreground font-normal"> / </span>}
                    <RenderedHtml html={answer} inline />
                  </React.Fragment>
                ))}
              </span>
            ) : null
          }
          ok={ok}
        />
      );
    }

    case 'MATCHING': {
      const given = asRecord(answerRaw);
      return (
        <div className="space-y-2">
          {(qd.matchPairs ?? []).map((pair: { key: string; value: string }) => {
            const userVal = given[pair.key] ?? given[pair.key.trim()] ?? '';
            const ok = Boolean(userVal) && norm(userVal) === norm(pair.value);
            return (
              <Row
                key={pair.key}
                label={<RenderedHtml html={pair.key} inline />}
                given={userVal ? <RenderedHtml html={userVal} inline /> : null}
                correct={<RenderedHtml html={pair.value} inline />}
                ok={ok}
              />
            );
          })}
        </div>
      );
    }

    case 'MULTIPLE_CHOICE':
    case 'MULTIPLE_RESPONSE':
    case 'TRUE_FALSE': {
      const options: Array<{ id: string; value?: string; text?: string }> = qd.options ?? [];
      const selected = Array.isArray(answerRaw)
        ? (answerRaw as string[])
        : typeof answerRaw === 'string' && answerRaw
          ? [answerRaw]
          : [];
      const correct = Array.isArray(correctAnswerRaw)
        ? (correctAnswerRaw as string[])
        : typeof correctAnswerRaw === 'string' && correctAnswerRaw
          ? [correctAnswerRaw]
          : [];
      const label = (id: unknown) => {
        const option = options.find((item) => item.id === id);
        return option?.value ?? option?.text ?? String(id);
      };
      const ok =
        correct.length > 0 &&
        selected.length === correct.length &&
        new Set(selected).size === selected.length &&
        selected.every((id) => correct.includes(id));

      const renderTokens = (ids: string[]) => (
        <div className="flex flex-wrap gap-1.5">
          {ids.map((id, idx) => (
            <span
              key={id || idx}
              className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-card px-2 py-0.5 text-xs font-medium"
            >
              <RenderedHtml html={label(id)} inline />
            </span>
          ))}
        </div>
      );

      return (
        <Row
          given={selected.length > 0 ? renderTokens(selected) : null}
          correct={correct.length > 0 ? renderTokens(correct) : null}
          ok={selected.length === 0 ? false : ok}
        />
      );
    }

    case 'PASSAGE': {
      return (
        <PassageReview
          questionData={qd}
          answerRaw={answerRaw}
          correctAnswerRaw={correctAnswerRaw}
          renderSubQuestionFooter={renderSubQuestionFooter}
        />
      );
    }

    default:
      return null;
  }
};

export default QuestionAnswerReview;

